import React from 'react';
import type {FormatXMLElementFn} from '@sail/intl';
import {message} from '@sail/intl';
import type {OpenapiAccountTypeEnum} from '@stripe-internal/data-v1/schema/types';
import {useMetrics} from '@sail/observability';
import {DebitCardForm} from '../DebitCardForm';
import {FormExplanation} from './FormExplanation';
import {BankAccountVerification} from './BankAccountVerification';
import {ExternalAccountConfirmationFooter} from './ExternalAccountConfirmationFooter';
import {PreviousExternalAccountNumber} from './PreviousExternalAccountNumber';
import {VirtualAccountConfirmation} from './VirtualAccountConfirmation';

import type {BankAccountExternalAccountFragmentData} from '../queries/bankAccountExternalAccountFragment';
import type {CardExternalAccountFragmentData} from '../queries/cardExternalAccountFragment';
import type {BlockchainAddressExternalAccountFragmentData} from '../queries/blockchainAddressExternalAccountFragment';

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const externalAccountMessages = {
  previousAccountNumberLabel: message({
    id: 'connectEmbedLib.externalAccount.previousAccountNumberLabel',
    description: 'Label above redacted previous bank account number.',
    defaultMessage: 'Previous account number',
  }),

  previousCardNumberLabel: message({
    id: 'connectEmbedLib.externalAccount.previousCardNumberLabel',
    description: 'Label above redacted previous debit card number.',
    defaultMessage: 'Previous card number',
  }),

  externalAccountField: message({
    id: 'connectEmbedLib.externalAccount.externalAccountField',

    description:
      'Prompt to ask the user to input their external account number.',

    defaultMessage: 'Confirm previous account number',
  }),

  externalAccountFieldClarification: message<{
    Link: FormatXMLElementFn;
  }>({
    id: 'connectEmbedLib.externalAccount.externalAccountFieldClarification',

    description:
      'Prompt to explain to the user why we ask to input their external account number.',

    defaultMessage:
      "We need your prior bank account information in order to update your payout information. If you've lost access to it, we can help <Link>on our support website</Link>.",
  }),

  externalAccountIdentityVerification: message<{
    Link: FormatXMLElementFn;
  }>({
    id: 'connectEmbedLib.externalAccount.externalAccountIdentityVerification',

    description:
      'Alternative prompt to explain to the user why we ask to input their external account number allowing them to verify their identity by scanning their id instead of confirming their previous external account number.',

    defaultMessage:
      "We need your prior bank account information in order to update your payout information. If you've lost access to it, <Link>you can verify your identity instead</Link>.",
  }),

  debitCardLabel: message({
    id: 'connectEmbedLib.externalAccount.externalAccountConfirmation.debitCardLabel',
    description: 'Label above input for previous debit card number.',
    defaultMessage: 'Confirm previous card number',
  }),
};

type Props = {
  onSubmitCode: (tokenizedExternalAccount: string) => Promise<void>;
  onVerifyIdentity?: () => void;
  isEligibleForIdentityVerification?: boolean;
  onClose: () => void;
  externalAccount: ExternalAccount;
  accountType?: OpenapiAccountTypeEnum;
  sending: boolean;
  error?: Error | null;
};

export type ExternalAccount =
  | BankAccountExternalAccountFragmentData
  | CardExternalAccountFragmentData
  | BlockchainAddressExternalAccountFragmentData;

export const ExternalAccountConfirmation = ({
  onSubmitCode,
  onVerifyIdentity,
  isEligibleForIdentityVerification,
  sending,
  error,
  externalAccount,
  accountType,
  onClose,
}: Props) => {
  const metrics = useMetrics();

  React.useEffect(() => {
    metrics.increment(
      'connect.auth.external_account_verification.verification.form_view',
      {challenge_type: 'dashboard_form'},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBankAccountSave: (
    accountNumber: string,
  ) => Promise<void> = async (accountNumber) => {
    // onSubmitCode can take a token or a raw account number, tokenization was removed from bank account verification intentionally because it fails for deny-listed bank accounts
    // bank accounts are deny-listed automatically on failed payout, so it's important we don't make changing the bank account after failed payout impossible
    // https://confluence.corp.stripe.com/display/T2RC/Blocklisted+Bank+Accounts+-+ACH+Debit+Declines+and+Failed+Payouts
    // Tokenization of bank accounts is unnecessary for stripe-surfaces, since we own this frontend surface and are not storing this data on the client
    onSubmitCode(accountNumber);
  };

  const handleDebitCardSave: ({
    token,
  }: {
    token: string;
  }) => Promise<void> = async ({token}) => {
    onSubmitCode(token);
  };

  if (externalAccount.object === 'card' && externalAccount.country) {
    // Country field required to use DebitCardForm
    return (
      <DebitCardForm
        buttonProps={{
          type: 'custom',
          onSuccess: handleDebitCardSave,
          renderCustomButton: (
            handleSubmit: () => Promise<void>,
            submitDisabled: boolean,
          ) => (
            <ExternalAccountConfirmationFooter
              onSubmit={handleSubmit}
              onClose={onClose}
              sending={sending}
              disabled={submitDisabled}
            />
          ),
        }}
        country={externalAccount.country}
        header={
          <FormExplanation
            identityVerificationEnabled={isEligibleForIdentityVerification}
            accountType={accountType}
            onVerifyIdentity={onVerifyIdentity}
          />
        }
        renderContent={() => (
          <PreviousExternalAccountNumber externalAccount={externalAccount} />
        )}
        label={externalAccountMessages.debitCardLabel}
        usageError={error?.message}
        clientIdentifier="ExternalAccountConfirmation"
      />
    );
  } else if (
    externalAccount.object === 'bank_account' &&
    externalAccount.can_confirm
  ) {
    return (
      <BankAccountVerification
        onSave={handleBankAccountSave}
        onClose={onClose}
        error={error?.message}
        sending={sending}
        identityVerificationEnabled={isEligibleForIdentityVerification}
        onVerifyIdentity={onVerifyIdentity}
        externalAccount={externalAccount}
        accountType={accountType}
      />
    );
  } else {
    return (
      <VirtualAccountConfirmation
        identityVerificationEnabled={isEligibleForIdentityVerification}
        onVerifyIdentity={onVerifyIdentity}
      />
    );
  }
};
